import { forwardRef, MouseEventHandler, useEffect, useState } from "react";
import {
  FormikComputedProps,
  FormikHandlers,
  FormikHelpers,
  FormikState,
  useFormik,
} from "formik";
import Grid from "@/components/Grid";
import Typography from "@/components/Typography";
import TextField from "@/components/TextField";
import { FormActions } from "@/components/FormController";
import DatePicker from "@/components/DatePicker";
import styles from "./PersonalDetailsForm.module.scss";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import {
  QuestionTextLoader,
  RadioLoader,
  TextFieldLoader as FormLoader,
} from "./PersonalDetailsFormLoader";
import Radio, { RadioGroup } from "@/components/Radio";
import { FormControlLabel } from "@/components/FormController";
import {
  DepartureDateFormType,
  Status,
} from "@/components/PersonalDetailsForms";
import {
  usePersonalDetailsFormInitialValues,
  usePersonalDetailsFormSubmission,
  usePersonalDetailsFormValidation,
} from "@/components/PersonalDetailsForms/hooks";
import { AnswerItem } from "./PersonalDetailsQuestionHelper";
import dayjs from "dayjs";

interface FormTemplateProp<FormType> {
  isFormSubmitting: boolean;
  formik: FormikState<FormType> &
    FormikComputedProps<FormType> &
    FormikHelpers<FormType> &
    FormikHandlers;
  isFormDataLoading: boolean;
  disabled: boolean;
}

function DepartureDateFormTemplateWithRef<T extends DepartureDateFormType>(
  {
    isFormSubmitting,
    formik,
    isFormDataLoading,
    disabled,
  }: FormTemplateProp<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const onDateChange = (date: dayjs.Dayjs | null) => {
    formik.setValues((values) => ({
      ...values,
      departureDate: date ? date.utc().startOf("day").unix() : null,
    }));
  };

  return (
    <div ref={ref}>
      <Grid
        container
        spacing="16px"
        columnSpacing="12px"
        className={styles.gridContainer}
      >
        <Grid item>
          {isFormDataLoading ? (
            FormLoader
          ) : (
            <DatePicker
              clearable
              label="Departure Date"
              views={["year", "month"]}
              disabled={isFormSubmitting || disabled}
              value={
                formik.values.departureDate
                  ? dayjs(formik.values.departureDate * 1000)
                      .utc()
                      .startOf("day")
                  : null
              }
              onChange={(date) => onDateChange(date as dayjs.Dayjs)}
              renderInput={(params) => (
                <TextField
                  fullWidth
                  {...params}
                  error={
                    formik.touched.departureDate &&
                    Boolean(formik.errors.departureDate)
                  }
                  helperText={
                    formik.touched.departureDate && formik.errors.departureDate
                  }
                />
              )}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

const DepartureDateForm: React.FC<{
  onSubmitted?: () => void;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  secondaryButtonAction?: MouseEventHandler<HTMLButtonElement> | undefined;
  correlationId?: string;
  onSubmitting?: (isFormSubmitting: boolean) => void;
}> = ({
  onSubmitted,
  primaryButtonLabel = "Save",
  secondaryButtonLabel,
  secondaryButtonAction,
  correlationId = "",
  onSubmitting,
}) => {
  const [isRefetchingForm, setRefetchingForm] = useState(false);
  const { data: personalDetailsData, refetch: personalDetailsRefetch } =
    usePersonalDetailsQuery();

  const { workingHolidayVisaValidationSchema } =
    usePersonalDetailsFormValidation();

  const { submitWorkingHolidayVisaForm, isUpdatingWorkingHolidayVisa } =
    usePersonalDetailsFormSubmission();

  const { departureDateInitialValues, isLoading: isInitialDataLoading } =
    usePersonalDetailsFormInitialValues();

  const isFormDataLoading = isInitialDataLoading;
  const isFormSubmitting = isUpdatingWorkingHolidayVisa || isRefetchingForm;

  useEffect(() => {
    onSubmitting?.(isFormSubmitting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormSubmitting]);

  const onSubmit = async (values: DepartureDateFormType) => {
    try {
      if (formik.dirty) {
        //todo
      }

      setRefetchingForm(true);
      await Promise.all([personalDetailsRefetch()]);
      onSubmitted?.();
    } catch (error) {
      console.error(error);
    } finally {
      setRefetchingForm(false);
    }
  };

  const initialValues = departureDateInitialValues;

  const formik = useFormik({
    initialValues,
    validationSchema: workingHolidayVisaValidationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (!personalDetailsData) return;
    formik.resetForm({ values: initialValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetailsData]);

  return (
    <>
      <DepartureDateFormTemplate
        formik={formik}
        isFormDataLoading={isFormDataLoading}
        isFormSubmitting={isFormSubmitting}
        disabled={false}
      />

      <FormActions
        primaryButton={{
          label: primaryButtonLabel,
          action: () => formik.handleSubmit(),
          isLoading: isFormSubmitting,
          isDisabled: Boolean(onSubmitted) ? isFormSubmitting : !formik.dirty,
          className: styles.submitButton,
        }}
        secondaryButton={{
          label: secondaryButtonLabel,
          action: secondaryButtonAction,
          className: styles.secondaryButton,
        }}
      />
    </>
  );
};
const DepartureDateFormTemplate = forwardRef(DepartureDateFormTemplateWithRef);

export { DepartureDateFormTemplate };
export default DepartureDateForm;
